import React, { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinner, faChevronRight } from '@fortawesome/free-solid-svg-icons';
import API_URL from '../config';

const CHANNEL_ROLES = [
  { key: 'frontal_eeg',  label: 'Frontal EEG',  required: true,  hint: 'e.g. F4-M1, F3-M2, EEG F4' },
  { key: 'central_eeg',  label: 'Central EEG',  required: false, hint: 'e.g. C4-M1, C3-M2, EEG C4' },
  { key: 'occipital_eeg',label: 'Occipital EEG',required: false, hint: 'e.g. O2-M1, O1-M2, EEG O2' },
  { key: 'right_eog',    label: 'Right EOG',    required: false, hint: 'e.g. E2-M1, EOG Right' },
  { key: 'left_eog',     label: 'Left EOG',     required: false, hint: 'e.g. E1-M2, EOG Left' },
  { key: 'chin_emg',     label: 'Chin EMG',     required: false, hint: 'e.g. 2-F, EMG chin' },
];

const ChannelMappingModal = ({ recordingId, channels, onConfirm, onCancel }) => {
  const [mapping, setMapping] = useState(() => {
    // Auto-assign channels using common name patterns.
    const initial = {};
    const patterns = {
      frontal_eeg:   [/^F[34][-_]?M[12]/i, /frontal/i, /^EEG F[34]/i],
      central_eeg:   [/^C[34][-_]?M[12]/i, /central/i, /^EEG C[34]/i],
      occipital_eeg: [/^O[12][-_]?M[12]/i, /occipital/i, /^EEG O[12]/i],
      right_eog:     [/E2[-_]?M1/i, /eog.?right/i, /roc/i],
      left_eog:      [/E1[-_]?M2/i, /eog.?left/i, /loc/i],
      chin_emg:      [/chin/i, /emg/i, /^[12][-_][F2]/i],
    };
    const assigned = new Set();
    for (const role of CHANNEL_ROLES) {
      for (const pat of patterns[role.key]) {
        const match = channels.find(ch => pat.test(ch) && !assigned.has(ch));
        if (match) {
          initial[role.key] = match;
          assigned.add(match);
          break;
        }
      }
      if (!initial[role.key]) initial[role.key] = '';
    }
    return initial;
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (role, value) => {
    setMapping(prev => ({ ...prev, [role]: value }));
    setError('');
  };

  const handleSubmit = async () => {
    if (!mapping.frontal_eeg) {
      setError('A Frontal EEG channel is required.');
      return;
    }
    setSubmitting(true);
    // Strip empty values from the mapping before sending.
    const cleanMapping = Object.fromEntries(
      Object.entries(mapping).filter(([, v]) => v)
    );
    try {
      const response = await fetch(`${API_URL}/api/recording/${recordingId}/start-processing/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
        },
        body: JSON.stringify({ channel_mapping: cleanMapping }),
      });
      if (response.ok) {
        onConfirm();
      } else {
        const data = await response.json();
        setError(data.error || 'Failed to start processing.');
        setSubmitting(false);
      }
    } catch (err) {
      setError('Network error. Please try again.');
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
        <div className="px-6 py-5 border-b border-gray-100 dark:border-gray-700">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Assign EDF Channels</h2>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Select which channel from the EDF file corresponds to each signal type.
            Only <span className="font-medium text-gray-700 dark:text-gray-300">Frontal EEG</span> is required.
          </p>
        </div>

        <div className="px-6 py-4 space-y-3 max-h-[60vh] overflow-y-auto">
          {CHANNEL_ROLES.map(({ key, label, required, hint }) => (
            <div key={key} className="flex items-center gap-4">
              <div className="w-36 shrink-0">
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">{label}</span>
                {required && <span className="ml-1 text-red-500 text-xs">*</span>}
              </div>
              <select
                value={mapping[key] || ''}
                onChange={e => handleChange(key, e.target.value)}
                className="flex-1 text-sm rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">— not assigned —</option>
                {channels.map(ch => (
                  <option key={ch} value={ch}>{ch}</option>
                ))}
              </select>
            </div>
          ))}
          {error && (
            <p className="text-sm text-red-600 dark:text-red-400 pt-1">{error}</p>
          )}
        </div>

        <div className="px-6 py-4 border-t border-gray-100 dark:border-gray-700 flex justify-end gap-3">
          <button
            onClick={onCancel}
            disabled={submitting}
            className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-lg transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={submitting}
            className="px-5 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 rounded-lg transition-colors flex items-center gap-2"
          >
            {submitting ? (
              <><FontAwesomeIcon icon={faSpinner} spin /> Starting…</>
            ) : (
              <>Start Processing <FontAwesomeIcon icon={faChevronRight} /></>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ChannelMappingModal;
